import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Navigation, MapPin, User, Plus, Package, Flag } from 'lucide-react';
import EntityTile, { TilePill } from './EntityTile';
import ReportModal from './ReportModal';
import SmartImg from './SmartImg';

/**
 * Unified tile for items shown across Discover, Friends → Tools, and
 * Community → Tools. Wraps the shared `<EntityTile>` so every surface
 * stays visually uniform — a design change there propagates here.
 *
 * Expects `item` with: id, title, photo, category, description,
 * owner_name, owner_photo, distance, is_friend, is_community.
 *
 * `onBorrow(item)` is required for friend-tier items; `onStartProject(item)`
 * is used for non-friend items (Discover) when provided.
 */
export const categoryColors = {
  'Power Tools': 'bg-orange-50 text-orange-700',
  'Hand Tools': 'bg-amber-50 text-amber-700',
  'Garden Tools': 'bg-green-50 text-green-700',
  'Ladders & Scaffolding': 'bg-yellow-50 text-yellow-700',
  'Painting Supplies': 'bg-indigo-50 text-indigo-700',
  'Plumbing': 'bg-blue-50 text-blue-700',
  'Electrical': 'bg-yellow-50 text-yellow-700',
  'Camping & Outdoor': 'bg-emerald-50 text-emerald-700',
  'Sports Equipment': 'bg-red-50 text-red-700',
  'Bikes & Scooters': 'bg-cyan-50 text-cyan-700',
  'Yard Games': 'bg-lime-50 text-lime-700',
  'Fishing & Hunting': 'bg-teal-50 text-teal-700',
  'Kitchen Appliances': 'bg-rose-50 text-rose-700',
  'Small Appliances': 'bg-pink-50 text-pink-700',
  'Party Supplies': 'bg-violet-50 text-violet-700',
  'Electronics': 'bg-purple-50 text-purple-700',
  'Cameras & AV': 'bg-indigo-50 text-indigo-700',
  'Gaming': 'bg-violet-50 text-violet-700',
  'Moving Equipment': 'bg-slate-50 text-slate-700',
  'Storage & Organization': 'bg-zinc-50 text-zinc-700',
  'Baby & Kids Gear': 'bg-sky-50 text-sky-700',
  'Toys & Games': 'bg-pink-50 text-pink-700',
  'Books & Media': 'bg-amber-50 text-amber-700',
  'Holiday Decorations': 'bg-red-50 text-red-700',
  'Seasonal Items': 'bg-orange-50 text-orange-700',
  'Cleaning Equipment': 'bg-blue-50 text-blue-700',
  'Safety Equipment': 'bg-yellow-50 text-yellow-700',
  'Musical Instruments': 'bg-purple-50 text-purple-700',
  'Furniture': 'bg-stone-100 text-stone-700',
  'Clothing & Accessories': 'bg-rose-50 text-rose-700',
  'Pet Supplies': 'bg-amber-50 text-amber-700',
  'Other': 'bg-stone-100 text-stone-600',
};

function SourceBadge({ item }) {
  if (item.is_friend) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-nunito font-semibold whitespace-nowrap">
      <Users size={9} /> Friend
    </span>
  );
  if (item.is_community) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-nunito font-semibold whitespace-nowrap">
      <Users size={9} /> Community
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-nunito whitespace-nowrap">
      <Navigation size={9} /> Public
    </span>
  );
}

function OwnerRow({ item }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-nunito">
      <SmartImg
        src={item.owner_photo}
        alt={item.owner_name}
        className="w-4 h-4 rounded-full object-cover flex-shrink-0"
        fallbackClassName="w-4 h-4 rounded-full bg-stone-200 flex-shrink-0"
        fallback={<User size={8} className="text-stone-500" />}
      />
      <span className="truncate">{item.owner_name}</span>
      {item.distance !== null && item.distance !== undefined && (
        <span className="flex items-center gap-0.5 text-blue-600 flex-shrink-0">
          <MapPin size={9} />{item.distance}mi
        </span>
      )}
    </div>
  );
}

export default function ToolCard({ item, onBorrow, onStartProject }) {
  const [reportOpen, setReportOpen] = useState(false);
  const borderClass = item.is_friend
    ? 'border-2 border-orange-400 hover:shadow-md'
    : item.is_community
      ? 'border border-green-200/70 hover:shadow-md'
      : 'border border-white/60 hover:shadow-md';

  const primaryCta = item.is_friend ? (
    <button
      onClick={() => onBorrow?.(item)}
      data-testid={`borrow-${item.id}`}
      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs py-2 rounded-lg font-nunito font-semibold transition-all"
    >
      Request to Borrow
    </button>
  ) : onStartProject ? (
    <button
      onClick={() => onStartProject(item)}
      data-testid={`start-project-${item.id}`}
      className="flex-1 bg-[#004B00] hover:bg-[#003800] text-white text-xs py-2 rounded-lg font-nunito font-semibold transition-all flex items-center justify-center gap-1"
    >
      <Plus size={11} /> Start a Project
    </button>
  ) : (
    <button
      onClick={() => onBorrow?.(item)}
      data-testid={`borrow-${item.id}`}
      className="flex-1 bg-[#004B00] hover:bg-[#003800] text-white text-xs py-2 rounded-lg font-nunito font-semibold transition-all"
    >
      Request to Borrow
    </button>
  );

  return (
    <>
      <EntityTile
        testId={`feed-item-${item.id}`}
        borderClass={borderClass}
        header={<>
          <Link to={`/items/${item.id}`} className="flex-1 min-w-0 hover:text-[#004B00] transition-colors">
            <h3 className="font-poppins font-semibold text-gray-900 text-sm line-clamp-2 leading-snug">{item.title}</h3>
          </Link>
          <SourceBadge item={item} />
        </>}
        photo={item.photo}
        photoHref={`/items/${item.id}`}
        body={<>
          {item.description ? (
            <p className="text-xs text-gray-500 font-nunito line-clamp-2 leading-snug">{item.description}</p>
          ) : (
            <p className="text-xs text-gray-300 font-nunito italic">No description</p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {item.category && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-nunito whitespace-nowrap ${categoryColors[item.category] || 'bg-stone-100 text-stone-600'}`}>
                {item.category}
              </span>
            )}
            {item.condition && <TilePill variant="gray">{item.condition}</TilePill>}
          </div>
          <div className="mt-auto pt-1"><OwnerRow item={item} /></div>
        </>}
        footer={<>
          {primaryCta}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setReportOpen(true); }}
            data-testid={`report-item-btn-${item.id}`}
            title="Report this item"
            aria-label="Report this item"
            className="flex-shrink-0 p-2 rounded-lg border border-stone-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-all"
          >
            <Flag size={12} />
          </button>
        </>}
      />
      {reportOpen && (
        <ReportModal
          targetType="item"
          targetId={item.id}
          targetName={item.title}
          onClose={() => setReportOpen(false)}
        />
      )}
    </>
  );
}
